import {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {ColumnDef} from '@tanstack/react-table';
import {DateTime} from 'luxon';
import {Button as BootstrapButton, Form} from 'react-bootstrap';
import Badge from '../../../../../app/common/components/Badge';
import TableMetronic from '../../../../../app/common/components/table/TableMetronic';
import {AppelHistorique} from '../TrunkSip/mocks';
import {useGetHistoriqueAppels} from '../../../Hooks/TelephonieManager';
import Loading from '../../../../../app/common/components/Loading';

/**
 * MAQUETTE — reproduction de « Symphonie VOIP — Historique des appels » : filtre par
 * période (appliqué au clic sur « Rafraîchir », pas en direct pendant la saisie),
 * export CSV de la période affichée, tableau triable/paginable (réutilise
 * TableMetronic, déjà utilisé ailleurs dans le domaine Téléphonie).
 */

const formaterDuree = (secondes: number): string => {
  const minutes = Math.floor(secondes / 60);
  const reste = secondes % 60;
  return `${String(minutes).padStart(2, '0')}:${String(reste).padStart(2, '0')}`;
};

/** Un segment court (numéro interne, groupe...) s'affiche en badge bleu, une destination externe/brute en badge sombre. */
const estSegmentCourt = (segment: string): boolean => /^[0-9]{2,5}$/.test(segment);

const HistoriqueAppels = (): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading, refetch} = useGetHistoriqueAppels();
  const [dateDebutEditee, setDateDebutEditee] = useState(
    DateTime.now().minus({days: 1}).toISODate() ?? ''
  );
  const [dateFinEditee, setDateFinEditee] = useState(DateTime.now().toISODate() ?? '');
  const [periode, setPeriode] = useState({debut: dateDebutEditee, fin: dateFinEditee});

  const columns = useMemo<ColumnDef<AppelHistorique>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.COLUMN.DATE'}),
        accessorKey: 'dateHeure',
        cell: ({getValue}) => DateTime.fromISO(getValue<string>()).toFormat('dd/MM/yyyy HH:mm:ss'),
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.COLUMN.TYPE'}),
        accessorKey: 'type',
        enableSorting: false,
        cell: ({getValue}) => {
          const type = getValue<AppelHistorique['type']>();
          return (
            <Badge
              text={intl.formatMessage({
                id:
                  type === 'entrant'
                    ? 'TELEPHONIE.PBX.HISTORIQUE.TYPE.ENTRANT'
                    : 'TELEPHONIE.PBX.HISTORIQUE.TYPE.SORTANT',
              })}
              color={type === 'entrant' ? 'success' : 'dark'}
            />
          );
        },
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.COLUMN.APPELANT'}),
        accessorKey: 'appelant',
      },
      {
        id: 'destination',
        header: intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.COLUMN.DESTINATION'}),
        accessorFn: (row) => row.destination.join(' > '),
        cell: ({row}) => (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {row.original.destination.map((segment, index) => (
              <span key={index} className="d-flex align-items-center gap-2">
                {index > 0 && <i className="bi bi-chevron-right text-muted fs-8" />}
                <Badge text={segment} color={estSegmentCourt(segment) ? 'primary' : 'dark'} />
              </span>
            ))}
          </div>
        ),
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.COLUMN.RESULTAT'}),
        accessorKey: 'resultat',
        enableSorting: false,
        cell: ({getValue}) => {
          const resultat = getValue<AppelHistorique['resultat']>();
          return (
            <Badge
              text={intl.formatMessage({
                id:
                  resultat === 'reussi'
                    ? 'TELEPHONIE.PBX.HISTORIQUE.RESULTAT.REUSSI'
                    : 'TELEPHONIE.PBX.HISTORIQUE.RESULTAT.ABANDONNE',
              })}
              color={resultat === 'reussi' ? 'success' : 'dark'}
            />
          );
        },
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.COLUMN.DUREE'}),
        accessorKey: 'dureeSecondes',
        enableSorting: false,
        cell: ({getValue}) => formaterDuree(getValue<number>()),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl.locale]
  );

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  const appelsPeriode = data.filter((appel) => {
    const date = DateTime.fromISO(appel.dateHeure).toISODate();
    return date != null && date >= periode.debut && date <= periode.fin;
  });

  const exporterCsv = (): void => {
    const entetes = ['Date', 'Type', 'Appelant', 'Destination', 'Résultat', 'Durée'];
    const lignes = appelsPeriode.map((appel) => [
      DateTime.fromISO(appel.dateHeure).toFormat('dd/MM/yyyy HH:mm:ss'),
      intl.formatMessage({
        id:
          appel.type === 'entrant'
            ? 'TELEPHONIE.PBX.HISTORIQUE.TYPE.ENTRANT'
            : 'TELEPHONIE.PBX.HISTORIQUE.TYPE.SORTANT',
      }),
      appel.appelant,
      appel.destination.join(' > '),
      intl.formatMessage({
        id:
          appel.resultat === 'reussi'
            ? 'TELEPHONIE.PBX.HISTORIQUE.RESULTAT.REUSSI'
            : 'TELEPHONIE.PBX.HISTORIQUE.RESULTAT.ABANDONNE',
      }),
      formaterDuree(appel.dureeSecondes),
    ]);
    const contenu = [entetes, ...lignes].map((ligne) => ligne.join(';')).join('\n');
    const blob = new Blob([contenu], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = `historique-appels_${periode.debut}_${periode.fin}.csv`;
    document.body.appendChild(lien);
    lien.click();
    document.body.removeChild(lien);
    URL.revokeObjectURL(url);
  };

  return (
    <TableMetronic<AppelHistorique>
      title={intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.TITLE'})}
      subtitle={
        <p className="text-muted fs-6 mb-0 mt-4">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.DESCRIPTION'})}
        </p>
      }
      data={appelsPeriode}
      columns={columns}
      pagination
      stackedHeader
      defaultSorting={[{id: 'dateHeure', desc: true}]}
      elementName={intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.ELEMENT_NAME'})}
      elementNamePlural={intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.ELEMENT_NAME_PLURAL'})}
      toolbar={
        <div className="d-flex flex-wrap align-items-center gap-3">
          <span className="fs-6">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.PERIODE_DEBUT'})}
          </span>
          <Form.Control
            type="date"
            style={{maxWidth: 180}}
            value={dateDebutEditee}
            onChange={(e) => setDateDebutEditee(e.target.value)}
          />
          <span className="fs-6">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.PERIODE_FIN'})}
          </span>
          <Form.Control
            type="date"
            style={{maxWidth: 180}}
            value={dateFinEditee}
            onChange={(e) => setDateFinEditee(e.target.value)}
          />
          <BootstrapButton
            variant="dark"
            onClick={() => {
              setPeriode({debut: dateDebutEditee, fin: dateFinEditee});
              refetch();
            }}
          >
            {intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.RAFRAICHIR'})}
          </BootstrapButton>
        </div>
      }
      toolbarEnd={
        <BootstrapButton variant="primary" onClick={exporterCsv}>
          <i className="bi bi-download me-2" />
          {intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.EXPORTER_CSV'})}
        </BootstrapButton>
      }
    />
  );
};

export default HistoriqueAppels;
